import { getToken } from "./auth";
import { UploadSource } from "@/components/upload/types/upload-item";
import { WorkspaceFolder } from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";
import {
  GraphRequestError,
  GraphRetryLog,
  describeGraphFailure,
  graphFetch,
} from "./graphFetch";
import {
  CloudFileCheckStatus,
  CloudFileSyncFetch,
  FolderContentsResult,
  FolderMetadataResult,
  GraphApiChildItem,
  GraphApiResponse,
  FetchedFileInfo,
  FetchedFolderInfo,
} from "@/types/sync.types";

const GRAPH_API_BASE_URL = "https://graph.microsoft.com/v1.0";

/**
 * Scopes required for reading files from Graph API
 */
const READ_SCOPES = ["Files.Read", "Files.Read.All"];
const READ_SCOPES_WITH_USER = ["Files.Read", "Files.Read.All", "User.Read"];

/**
 * Builds a Graph API URL for accessing a drive item
 */
export const buildGraphApiItemUrl = (
  driveId: string | undefined,
  siteId: string | undefined,
  itemId: string
): string => {
  if (driveId) {
    return `${GRAPH_API_BASE_URL}/drives/${driveId}/items/${itemId}`;
  }
  if (siteId) {
    return `${GRAPH_API_BASE_URL}/sites/${siteId}/drive/items/${itemId}`;
  }
  throw new Error("Either driveId or siteId must be provided");
};

/**
 * Builds a URL for fetching children of a folder. Like buildGraphApiItemUrl it
 * prefers the drive: /sites/{siteId}/drive is only the site's default library.
 */
export const buildChildrenUrl = (
  driveId: string | undefined,
  siteId: string | undefined,
  itemId: string
): string => `${buildGraphApiItemUrl(driveId, siteId, itemId)}/children`;

/**
 * Builds a URL for downloading a file's content (drive first, as above)
 */
export const buildFileContentUrl = (
  driveId: string | undefined,
  siteId: string | undefined,
  itemId: string
): string => `${buildGraphApiItemUrl(driveId, siteId, itemId)}/content`;

/**
 * Acquires a Graph API access token
 */
export const getGraphToken = async (
  includeUserScope: boolean = false
): Promise<string | null> => {
  try {
    const token = await getToken({
      resource: "https://graph.microsoft.com",
      type: "Graph",
      scopes: includeUserScope ? READ_SCOPES_WITH_USER : READ_SCOPES,
    });
    return token;
  } catch {
    // getToken logs every failure (a declined consent as a warning).
    return null;
  }
};

/**
 * Per-call options shared by every Graph call made during a sync.
 */
export type GraphCallOptions = {
  signal?: AbortSignal;
  /** Collects recovered retries so a sync logs one warning, not one per call. */
  retryLog?: GraphRetryLog;
};

/** Fields change identification needs; never the file content. */
const CHILD_SELECT_FIELDS =
  "id,name,lastModifiedDateTime,file,folder,parentReference";

/**
 * Fetches metadata for a folder from SharePoint/OneDrive
 */
export const fetchFolderMetadata = async (
  folder: WorkspaceFolder,
  options: GraphCallOptions = {}
): Promise<FolderMetadataResult> => {
  if (!folder.sharepointFileId || !folder.driveId) {
    return null;
  }

  try {
    const token = await getGraphToken();

    if (!token) {
      return null;
    }

    const url = buildGraphApiItemUrl(
      folder.driveId,
      folder.siteId,
      folder.sharepointFileId
    );

    const response = await graphFetch(url, token, {
      ...options,
      operation: "fetchGraphFolderMetadata",
    });

    const data = await response.json();
    return {
      name: data.name,
      lastModifiedDateTime: data.lastModifiedDateTime,
    };
  } catch (error) {
    logger.error("Failed to fetch folder metadata from Microsoft Graph", {
      operation: "fetchGraphFolderMetadata",
      error,
      status: error instanceof GraphRequestError ? error.status : undefined,
      folderId: folder.id,
      driveId: folder.driveId,
      siteId: folder.siteId,
      itemId: folder.sharepointFileId,
    });
    return null;
  }
};

/**
 * Fetches all children of a folder, following pagination. Throws a
 * GraphRequestError when any page fails: a partial listing would make the
 * files on the missing pages look deleted.
 */
export const fetchAllChildren = async (
  childrenUrl: string,
  token: string,
  options: GraphCallOptions = {}
): Promise<GraphApiChildItem[]> => {
  const allChildren: GraphApiChildItem[] = [];
  let nextLink: string | null =
    `${childrenUrl}?$select=${CHILD_SELECT_FIELDS}&$top=1000`;

  while (nextLink) {
    const response: Response = await graphFetch(
      nextLink,
      token,
      { ...options, operation: "fetchGraphFolderContents" },
      { headers: { Accept: "application/json" } }
    );

    const data: GraphApiResponse = await response.json();
    allChildren.push(...(data.value || []));
    nextLink = data["@odata.nextLink"] || null;
  }

  return allChildren;
};

/**
 * Downloads the content of one drive item. Throws a GraphRequestError on
 * failure.
 */
export const downloadDriveItemContent = async (
  ids: { driveId?: string; siteId?: string; sharepointFileId: string },
  fileName: string,
  options: GraphCallOptions = {}
): Promise<File> => {
  const token = await getGraphToken(true);
  if (!token) {
    throw GraphRequestError.tokenUnavailable();
  }

  const response = await graphFetch(
    buildFileContentUrl(ids.driveId, ids.siteId, ids.sharepointFileId),
    token,
    { ...options, operation: "downloadGraphFile" }
  );
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};

/**
 * Lists the files and subfolders of a folder on SharePoint/OneDrive, without
 * downloading any content (new files are downloaded when they are uploaded).
 * Handles both OneDrive (where siteId can be null) and SharePoint.
 *
 * Throws when the listing can't be read completely.
 */
export const fetchFolderContentsDirect = async (
  folder: WorkspaceFolder,
  options: GraphCallOptions = {}
): Promise<FolderContentsResult> => {
  if (!folder.sharepointFileId || !folder.driveId) {
    return { files: [], folders: [] };
  }

  try {
    const token = await getGraphToken(true);

    if (!token) {
      throw GraphRequestError.tokenUnavailable();
    }

    const childrenUrl = buildChildrenUrl(
      folder.driveId,
      folder.siteId,
      folder.sharepointFileId
    );

    const allChildren = await fetchAllChildren(childrenUrl, token, options);

    const files: FetchedFileInfo[] = [];
    const folders: FetchedFolderInfo[] = [];

    const folderSource = folder.siteId
      ? UploadSource.SHAREPOINT
      : UploadSource.ONEDRIVE;

    for (const child of allChildren) {
      if (child.folder) {
        folders.push({
          id: child.id,
          name: child.name,
          driveId: child.parentReference?.driveId || folder.driveId,
          siteId: child.parentReference?.siteId || folder.siteId,
          sharepointFileId: child.id,
        });
      } else if (child.file) {
        files.push({
          id: child.id,
          name: child.name,
          driveId: child.parentReference?.driveId || folder.driveId,
          siteId: child.parentReference?.siteId || folder.siteId,
          sharepointFileId: child.id,
          source: folderSource,
          lastModifiedDateTime: child.lastModifiedDateTime,
        });
      }
    }

    return { files, folders };
  } catch (error) {
    logger.error("Failed to fetch folder contents from Microsoft Graph", {
      operation: "fetchGraphFolderContents",
      error,
      status: error instanceof GraphRequestError ? error.status : undefined,
      folderId: folder.id,
      driveId: folder.driveId,
      siteId: folder.siteId,
      itemId: folder.sharepointFileId,
    });
    throw error;
  }
};

/**
 * Reads a linked file's metadata and downloads its content only when
 * `shouldDownload(cloudLastModified)` says so. Never throws: throttling, an
 * outage or the network come back as `failed`; only a 404, 403 or other 4xx
 * as `missing`.
 */
export const fetchLinkedFileForSync = async (
  ids: { driveId?: string; siteId?: string; sharepointFileId: string },
  fallbackName: string,
  token: string,
  shouldDownload: (cloudLastModified: number) => boolean,
  options: GraphCallOptions & { documentId?: string } = {}
): Promise<CloudFileSyncFetch> => {
  const operation = "downloadFileById";
  const { documentId, ...graphOptions } = options;
  try {
    const metadataResponse = await graphFetch(
      buildGraphApiItemUrl(ids.driveId, ids.siteId, ids.sharepointFileId),
      token,
      { ...graphOptions, operation },
      { headers: { Accept: "application/json" } }
    );
    const metadata: { name?: string; lastModifiedDateTime?: string } =
      await metadataResponse.json();
    const lastModified = new Date(
      metadata.lastModifiedDateTime ?? Number.NaN
    ).getTime();
    if (!shouldDownload(lastModified)) {
      return { kind: "unchanged" };
    }

    const contentResponse = await graphFetch(
      buildFileContentUrl(ids.driveId, ids.siteId, ids.sharepointFileId),
      token,
      { ...graphOptions, operation }
    );
    const blob = await contentResponse.blob();
    return {
      kind: "downloaded",
      file: new File([blob], metadata.name || fallbackName, {
        type: blob.type,
        lastModified,
      }),
    };
  } catch (error) {
    logger.error("Error downloading file by id from SharePoint/OneDrive", {
      operation,
      error,
      status: error instanceof GraphRequestError ? error.status : undefined,
      documentId,
      driveId: ids.driveId,
      siteId: ids.siteId,
      sharepointFileId: ids.sharepointFileId,
    });
    if (error instanceof GraphRequestError && error.kind !== "transient") {
      return { kind: "missing" };
    }
    return { kind: "failed", reason: describeGraphFailure(error) };
  }
};

/**
 * Checks the accessibility of a file on SharePoint/OneDrive.
 *
 * Returns:
 * - `exists`            — 200 OK; file is reachable
 * - `missing`           — 404; file confirmed not found
 * - `inaccessible`      — another 4xx (e.g. 403 Forbidden)
 * - `unknown`           — token failure, throttling (429), 5xx or a network
 *                         error; says nothing about the file
 */
export const checkCloudFileStatus = async (
  driveId: string | undefined,
  siteId: string | undefined,
  sharepointFileId: string,
  options: GraphCallOptions = {}
): Promise<CloudFileCheckStatus> => {
  try {
    const token = await getGraphToken();
    if (!token) return CloudFileCheckStatus.UNKNOWN;

    const url = buildGraphApiItemUrl(driveId, siteId, sharepointFileId);
    await graphFetch(url, token, {
      ...options,
      operation: "checkCloudFileStatus",
    });
    return CloudFileCheckStatus.EXISTS;
  } catch (error) {
    if (error instanceof GraphRequestError) {
      if (error.kind === "notFound") return CloudFileCheckStatus.MISSING;
      if (error.kind === "forbidden") return CloudFileCheckStatus.INACCESSIBLE;
    }
    logger.warn("Could not check whether a cloud file still exists", {
      operation: "checkCloudFileStatus",
      error,
      status: error instanceof GraphRequestError ? error.status : undefined,
      driveId,
      siteId,
      sharepointFileId,
    });
    return CloudFileCheckStatus.UNKNOWN;
  }
};

/**
 * @deprecated Use checkCloudFileStatus instead.
 * Kept for backwards compatibility — returns true only when the file is confirmed to exist.
 */
export const checkFileExists = async (
  driveId: string | undefined,
  siteId: string | undefined,
  sharepointFileId: string
): Promise<boolean> => {
  const status = await checkCloudFileStatus(driveId, siteId, sharepointFileId);
  return status === CloudFileCheckStatus.EXISTS;
};

/**
 * Determines the upload source based on siteId presence
 */
export const determineUploadSource = (siteId?: string): UploadSource => {
  return siteId ? UploadSource.SHAREPOINT : UploadSource.ONEDRIVE;
};

/**
 * Graph API service object for convenient access to all functions
 */
export const graphApiService = {
  buildItemUrl: buildGraphApiItemUrl,
  buildChildrenUrl,
  buildFileContentUrl,
  getToken: getGraphToken,
  fetchFolderMetadata,
  fetchFolderContentsDirect,
  downloadDriveItemContent,
  checkCloudFileStatus,
  checkFileExists,
  determineUploadSource,
};

export default graphApiService;
